import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useAuth } from '@saltus/auth';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import saveAtr from '../../actions/atr/saveAtr';
import submitAtr from '../../actions/atr/submitAtr';
import Loading from '../../components/loading/component';
import ProgressBar from '../../components/progress-bar/component';
import RiskForm from '../../components/risk-form/component';
import { GetQuestionsQuery } from '../../graphql/API';
import { getQuestions } from '../../graphql/queries';
import useQuery from '../../hooks/useQuery';
import { AtrState } from '../../reducers/atr/reducer';
import invariant from '../../utils/invariant';
import clearAtr from '../../actions/atr/clearAtr';
import { MapGetter, State } from '../../utils/map';
import { ContactSetState } from '../../reducers/contacts/interfaces';

interface QuestionnaireProps {
  contactId?: string;
  resourceId?: string;
}

const useStyles = makeStyles((theme) => ({
  content: {
    padding: '16px 0',
  },
  panel: {
    backgroundColor: theme.palette.common.backgroundHighlight,
  },
  title: {
    fontSize: theme.typography.pxToRem(55),
    marginBottom: 35,
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(34),
      marginBottom: 25,
    },
  },
}));

function Questionnaire(props: QuestionnaireProps) {
  const { contactId, resourceId } = props;

  const classes = useStyles();

  const [position, setPosition] = useState(1);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    data: answers,
    loading: isLoading,
    score,
  } = useSelector<
    {
      atr: AtrState;
    },
    AtrState
  >((state) => state.atr);

  const contact = useSelector<
    State<ContactSetState>,
    MapGetter<ContactSetState>
  >((state) => state.contacts.get('set'));

  const { user } = useAuth();

  const authMode = user ? 'userPool' : 'iam';

  const { data, loading } = useQuery<GetQuestionsQuery>(getQuestions, {
    authMode,
  });

  useEffect(() => {
    dispatch(clearAtr());
  }, [dispatch]);

  useEffect(() => {
    if (position > 13) {
      dispatch(
        submitAtr({
          responses: answers,
          authMode,
          ContactID: contactId ?? contact?.ContactID,
          resourceId,
        }),
      );
    }
  }, [
    answers,
    authMode,
    contact?.ContactID,
    contactId,
    dispatch,
    position,
    resourceId,
  ]);

  useEffect(() => {
    if (score > 0 && !!user) {
      navigate('/atr/results', {
        state: {
          score,
        },
      });
    }
  }, [score, navigate, user]);

  if (loading || isLoading) {
    return <Loading />;
  }

  return (
    <Grid container>
      {data?.getQuestions && (
        <Container maxWidth="lg">
          <Typography className={classes.title} align="center" variant="h1">
            Risk profiling
          </Typography>

          <Card className={classes.panel}>
            <CardContent className={classes.content}>
              <Container maxWidth="sm">
                <ProgressBar
                  label="Question #c of #t"
                  current={position}
                  total={data.getQuestions.length}
                />
              </Container>

              <Container maxWidth="md">
                {data.getQuestions.map((question, index) => {
                  invariant(question);
                  invariant(data.getQuestions);

                  return (
                    <Fragment key={question.id}>
                      {index === position - 1 && (
                        <RiskForm
                          id={question.id}
                          value={
                            answers.find((answer) => answer.id === question.id)
                              ?.answer.id
                          }
                          question={question.text}
                          options={question.answers.map((answer) => {
                            invariant(answer);

                            return answer;
                          })}
                          position={position}
                          total={data.getQuestions.length}
                          next={({ answer }) => {
                            if (answer) {
                              dispatch(
                                saveAtr({
                                  answer,
                                  id: question.id,
                                  question: question.text,
                                }),
                              );
                            }

                            setPosition(position + 1);
                          }}
                          previous={({ answer }) => {
                            if (answer) {
                              dispatch(
                                saveAtr({
                                  answer,
                                  id: question.id,
                                  question: question.text,
                                }),
                              );
                            }

                            setPosition(position - 1);
                          }}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </Container>
            </CardContent>
          </Card>
        </Container>
      )}
    </Grid>
  );
}

export default Questionnaire;
